import type { BuildGameInterviewState } from '@volley/crucible-ipc-types/build-game-types';

export type PlayerCountSource = 'quickAnswers' | 'slotAnswers' | 'unknown';

export interface PreviewControllerPolicy {
  minControllerSlots: number;
  defaultControllerSlots: number;
  maxControllerSlots: number;
  playerCountSource: PlayerCountSource;
  label: string;
}

export const HARD_PREVIEW_PHONE_CAP = 6;

function clampSlotCount(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.max(1, Math.min(HARD_PREVIEW_PHONE_CAP, Math.floor(value)));
}

function normalisePolicy(args: {
  min: number;
  defaultCount: number;
  max: number;
  source: PlayerCountSource;
  label: string;
}): PreviewControllerPolicy {
  const min = clampSlotCount(args.min);
  const max = Math.max(min, clampSlotCount(args.max));
  const defaultControllerSlots = Math.max(min, Math.min(max, clampSlotCount(args.defaultCount)));
  return {
    minControllerSlots: min,
    defaultControllerSlots,
    maxControllerSlots: max,
    playerCountSource: args.source,
    label: args.label,
  };
}

function parseStructuredPlayerCount(value: unknown): PreviewControllerPolicy | null {
  if (typeof value === 'number') {
    const count = clampSlotCount(value);
    return normalisePolicy({
      min: count,
      defaultCount: count,
      max: count,
      source: 'quickAnswers',
      label: `${count} player${count === 1 ? '' : 's'}`,
    });
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (
    /\b(?:unknown|unsure|not sure|tbd|depends|varies|variable|any|unlimited|audience|team|teams)\b/.test(
      lower,
    )
  ) {
    return null;
  }
  if (/\b\d+\s*\+|\b\d+\s+or more\b/.test(lower)) return null;
  if (/\b(?:solo|single[-\s]?player)\b/.test(lower)) {
    const count = 1;
    return normalisePolicy({
      min: count,
      defaultCount: count,
      max: count,
      source: 'quickAnswers',
      label: `${count} player`,
    });
  }
  const range = trimmed.match(/\b(\d+)\s*(?:-|to|–|—)\s*(\d+)\b/i);
  if (range) {
    const min = Number(range[1]);
    const max = Number(range[2]);
    if (Number.isFinite(min) && Number.isFinite(max) && min > 0 && max >= min) {
      return normalisePolicy({
        min,
        defaultCount: min,
        max,
        source: 'quickAnswers',
        label: `${min}-${max} players`,
      });
    }
  }
  const exact = trimmed.match(/\b(\d+)\b/);
  if (!exact) return null;
  const count = Number(exact[1]);
  if (!Number.isFinite(count) || count <= 0) return null;
  return normalisePolicy({
    min: count,
    defaultCount: count,
    max: count,
    source: 'quickAnswers',
    label: `${count} player${count === 1 ? '' : 's'}`,
  });
}

export function derivePreviewControllerPolicy(
  state: BuildGameInterviewState | null | undefined,
): PreviewControllerPolicy {
  const hint = state?.playerCountHint;
  if (hint) {
    return normalisePolicy({
      min: hint.minPlayers,
      defaultCount: hint.defaultPlayers,
      max: hint.maxPlayers,
      source: hint.source,
      label: hint.label,
    });
  }
  const quickAnswerPolicy = parseStructuredPlayerCount(state?.quickAnswers?.playerCount);
  if (quickAnswerPolicy) return quickAnswerPolicy;
  return {
    minControllerSlots: 1,
    defaultControllerSlots: 1,
    maxControllerSlots: HARD_PREVIEW_PHONE_CAP,
    playerCountSource: 'unknown',
    label: 'Unknown player count',
  };
}

export function normalisePreviewPhoneCapOverride(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return clampSlotCount(value);
}

export function applyPreviewPhoneCapOverride(
  policy: PreviewControllerPolicy,
  override: number | null | undefined,
): PreviewControllerPolicy {
  const cap = normalisePreviewPhoneCapOverride(override);
  if (cap === null) return policy;
  const defaultControllerSlots = Math.min(policy.defaultControllerSlots, cap);
  return {
    ...policy,
    minControllerSlots: Math.min(policy.minControllerSlots, defaultControllerSlots),
    defaultControllerSlots,
    maxControllerSlots: cap,
  };
}
